import { google, trpc } from '@tencent/tpro_rmonitor_product_manager_user';
import { get } from 'lodash-es';
import { userImplement } from '@/config/hooks/user';
import { BFFGet } from '@/infrastructure/axios';
import { API_PREFIX, BusiCode } from '@/infrastructure/axios/code';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';
import IGetDemoProductListRsp = trpc.rmonitor.product_manager.IGetDemoProductListRsp;
import IEmpty = google.protobuf.IEmpty;
import ProductType = trpc.rmonitor.product_manager.ProductType;

export type ICheckAdminReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckAdminReq>;
export type IVerifyCodeReq = WithTypeCast<trpc.rmonitor.product_manager.IVerifyCodeReq>;
export type ISendVerifyCodeReq = WithTypeCast<trpc.rmonitor.product_manager.ISendVerifyCodeReq>;
export type ICheckUserInProductReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckUserInProductReq>;
export type IUserInfo = WithTypeCast<trpc.rmonitor.product_manager.IUserInfo>;
export type IGetUsersReq = WithTypeCast<trpc.rmonitor.product_manager.IGetUsersReq>;
export type IGetUsersRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetUsersRsp>;
export type IQuitProductReq = WithTypeCast<trpc.rmonitor.product_manager.IQuitProductReq>;
export type IQuitProductRsp = WithTypeCast<trpc.rmonitor.product_manager.IQuitProductRsp>;
export type ICheckSuperAdminReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckSuperAdminReq>;
export type ICheckUserInProductRsp = WithTypeCast<trpc.rmonitor.product_manager.ICheckUserInProductRsp>;
export type ICollectUserInfoReq = WithTypeCast<trpc.rmonitor.product_manager.ICollectUserInfoReq>;
export type ICollectUserInfoRsp = WithTypeCast<trpc.rmonitor.product_manager.ICollectUserInfoRsp>;
export type IBindingBuglyqqFreeUserReq = WithTypeCast<trpc.rmonitor.product_manager.IBindingBuglyqqFreeUserReq>;
export type IBindingBuglyqqFreeUserRsp = WithTypeCast<trpc.rmonitor.product_manager.IBindingBuglyqqFreeUserRsp>;

export type IGetBuglyqqFreeProductInfosReq = WithTypeCast<trpc.rmonitor.product_manager.IGetBuglyqqFreeProductInfosReq>;
export type IGetBuglyqqFreeProductInfosRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetBuglyqqFreeProductInfosRsp>;

export type IGetNickNameByBuglyIDsReq = WithTypeCast<trpc.rmonitor.product_manager.IGetNickNameByBuglyIDsReq>;
export type IGetNickNameByBuglyIDsRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetNickNameByBuglyIDsRsp>;
export type IGetAccessTokenRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetAccessTokenRsp>;
export type IGetProductListByUserReq = WithTypeCast<trpc.rmonitor.product_manager.IGetProductListByUserReq>;
export type IGetProductListByUserRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetProductListByUserRsp>;
export type IProductInfo = WithTypeCast<trpc.rmonitor.product_manager.IProductInfo>;
export type FreeStatus = WithTypeCast<trpc.rmonitor.product_manager.FreeStatus>;
export const { FreeStatus } = trpc.rmonitor.product_manager;
export type IProductDetailInfo = WithTypeCast<trpc.rmonitor.product_manager.IProductDetailInfo>;

export interface ProductInfo {
  productId: string;
  productName: string;
  appKey: string;
  platformId: string;
  type?: number;
  department?: string;
  isFlutter?: boolean;
  logoUrl?: string;
  owner?: string;
  hasPermission?: boolean;
  isDemo?: boolean;
  bundleId?: string;
  diagnoseGuidAlias?: string;
  expireTime: string;
  freeStatus?: FreeStatus;
  expireDate?: string;
  productType?: ProductType;
}

export class UserService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.UserService,
    });
  }

  /**
   * 获取微信access_token
   */
  public getAccessToken = async (): Promise<string | null> => {
    try {
      const res: IGetAccessTokenRsp = await this.trpcPost('GetAccessToken');
      return res.accessToken ?? null;
    } catch (e) {
      return null;
    }
  };

  public getQrcode = async () => {
    try {
      const res = await BFFGet(`/${API_PREFIX}/api/wechat/qrcode`);
      return res || '';
    } catch (e) {
      return '';
    }
  };

  public getUserInfo = async (): Promise<IUserInfo | null> => {
    try {
      const res = await this.trpcPost('GetNoAuthUserInfo');

      return res.userInfo;
    } catch (e) {
      return null;
    }
  };

  /**
   * 获取带腾讯云实名认证信息的userInfo
   */
  public getAuthedUserInfo = async (): Promise<IUserInfo | null> => {
    try {
      const res = await this.trpcPost('GetUserInfo');

      return res.userInfo;
    } catch (e) {
      return null;
    }
  };

  /**
   * 修改用户信息
   * @param field
   * @param value
   */
  public async update(field: keyof IUserInfo, value: string | number): Promise<boolean> {
    try {
      await this.trpcPost('UpdateUserInfo', {
        userInfo: {
          [field]: value,
          buglyId: userImplement.getUserId(),
        },
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 获取登录用户产品列表
   */
  public getProductList = async (req?: IGetProductListByUserReq): Promise<IProductInfo[]> => {
    try {
      const res = await this.trpcPost<IGetProductListByUserRsp>('GetProductListByUser', req);

      return res.productInfos || [];
    } catch (e) {
      return [];
    }
  };

  /**
   * 发送手机/邮箱验证码
   * @param req
   * @param token
   */
  public sendVerifyCode = async (req: ISendVerifyCodeReq, token?: string): Promise<boolean> => {
    const config = token ? {
      headers: {
        Authorization: token,
      },
    } : undefined;
    try {
      await this.trpcPost('SendVerifyCode', req, config);
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * 校验并修改手机/邮箱
   * @param req
   * @param token
   */
  public verifyCode = async (req: IVerifyCodeReq, token?: string): Promise<boolean> => {
    const config = token ? {
      headers: {
        Authorization: token,
      },
    } : undefined;
    try {
      const res = await this.trpcPost('VerifyCode', req, config);

      return get(res, 'verification', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 判断用户是否是当前产品管理员
   * @param req
   */
  public checkIsAdmin = async (req: ICheckAdminReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost('CheckAdmin', req);

      return get(res, 'isAdmin', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 判断用户是否是超级管理员
   * @param req
   */
  public checkSuperAdmin = async (req: ICheckSuperAdminReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost('CheckSuperAdmin', req);

      return get(res, 'isSuperAdmin', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 用户是否有某个产品权限
   * @param req
   */
  public async canAccessProduct(req: ICheckUserInProductReq) {
    try {
      await this.trpcPost('CheckUserInProduct', req);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 按用户名模糊搜索接口
   */
  public async search(req: IGetUsersReq): Promise<IGetUsersRsp | null> {
    try {
      return await this.trpcPost('GetUsers', req);
    } catch (e) {
      return null;
    }
  }

  /**
   * 退出产品
   * @param req
   */
  public quit = async (req: IQuitProductReq): Promise<IQuitProductRsp> => {
    try {
      return await this.trpcPost('QuitProduct', req, {
        headers: {
          'X-ProductID': req.productId,
        },
      });
    } catch (e) {
      return {
        baseRsp: {
          code: e.message !== null ? Number(e.message) : e.message,
        },
      };
    }
  };

  /**
   * 校验用户是否可以访问产品
   */
  public checkUserInProduct = async (req: ICheckUserInProductReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost<ICheckUserInProductRsp>('CheckUserInProduct', req);
      return res?.isAuth || false;
    } catch (e) {
      return false;
    }
  };
  /**
   * 收集用户信息
   */
  public collectUserInfo =  async (req: ICollectUserInfoReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost<ICollectUserInfoRsp>('CollectUserInfo', req);
      return res?.baseRsp?.code === BusiCode.Ok;
    } catch (e) {
      return false;
    }
  };

  /**
   * 根据buglyID获取昵称
   */
  public getNickNameByBuglyIDs = async (req: IGetNickNameByBuglyIDsReq): Promise<Record<string, any>> => {
    try {
      const res = await this.trpcPost<IGetNickNameByBuglyIDsRsp>('GetNickNameByBuglyIDs', req);
      return res?.idToNick || {};
    } catch (e) {
      return {};
    }
  };

  /**
   * 获取demo产品
   */
  public getDemoProductList = async (req: IEmpty):
  Promise<IProductInfo[]> => {
    try {
      const res = await this.trpcPost<IGetDemoProductListRsp>('GetDemoProductList', req);
      return res?.productInfos || [];
    } catch (e) {
      return [];
    }
  };

  public bindOpenID = async (req: IBindingBuglyqqFreeUserReq, config?: any) => {
    try {
      return await this.trpcPost<IBindingBuglyqqFreeUserRsp>('BindingBuglyqqFreeUser', req, config);
    } catch (e) {
      return {};
    }
  };

  /**
   * 拉取指定用户免费版的所有的产品列表 (排除已经导入过来的)
   * @param req
   */
  public getBuglyqqFreeProductInfos = async (req: IGetBuglyqqFreeProductInfosReq): Promise<IProductInfo[]> => {
    try {
      const res = await this.trpcPost<IGetBuglyqqFreeProductInfosRsp>('GetBuglyqqFreeProductInfos', req);

      return res.productInfos ?? [];
    } catch (e) {
      return [];
    }
  };
}

export const userService = new UserService();
